import { useState, useEffect } from 'react';

interface WindowDimensions {
  width: number;
  height: number;
}

function getWindowDimensions(): WindowDimensions {
  const { innerWidth: width, innerHeight: height } =
    typeof window !== 'undefined' ? window : { innerWidth: 0, innerHeight: 0 };
  return {
    width,
    height,
  };
}

/**
 * Custom hook to get and track the window dimensions.
 * @returns {WindowDimensions} An object containing the current window width and height.
 */
export default function useWindowDimensions(): WindowDimensions {
  const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>(
    getWindowDimensions(),
  );

  useEffect(() => {
    function handleResize(): void {
      setWindowDimensions(getWindowDimensions());
    }

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Initial call to set dimensions on mount (though useState already handles initial)
    handleResize();

    // Cleanup function to remove the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array ensures this effect runs only once on mount and unmount

  return windowDimensions;
}
